// FAQ page — section-grouped Radix accordion + client-side search across
// both languages (so a TH user searching for "refund" still finds the
// matching question if they happen to phrase it in English, and vice versa).

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Search, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Input } from "@bloom/ui";
import { useLocale } from "../locale/locale-context.tsx";
import { getCatalog } from "@bloom/i18n";

// Each FAQ section's slug + the count of q/a pairs in the catalog. Adding a
// new question is a two-step process: append `faq.<section>.qN/aN` to both
// locales in the catalog, then bump the count here.
const SECTIONS = [
  { slug: "account", count: 5 },
  { slug: "payments", count: 5 },
  { slug: "watching", count: 5 },
  { slug: "content", count: 5 },
  { slug: "privacy", count: 5 },
] as const;

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  // Pre-joined lowercase blob across EN+TH used for substring search so we
  // match regardless of which locale the user typed in.
  haystack: string;
}

interface FaqSection {
  slug: string;
  heading: string;
  items: FaqItem[];
}

export function FaqPage() {
  const { t, locale } = useLocale();
  const [query, setQuery] = useState("");

  // Build the section list from the catalog. We pull *both* locales for the
  // search haystack so the filter works across language boundaries.
  const sections = useMemo<FaqSection[]>(() => {
    const en = getCatalog("en");
    const th = getCatalog("th");
    return SECTIONS.map((s) => ({
      slug: s.slug,
      heading: t(`faq.section.${s.slug}`),
      items: Array.from({ length: s.count }, (_, i) => {
        const n = i + 1;
        const qKey = `faq.${s.slug}.q${n}`;
        const aKey = `faq.${s.slug}.a${n}`;
        // `t` already returns the active-locale string. The haystack mixes
        // both locales so a typed English keyword matches even when the UI
        // is in Thai.
        return {
          id: `${s.slug}-${n}`,
          question: t(qKey),
          answer: t(aKey),
          haystack:
            `${en[qKey] ?? ""} ${en[aKey] ?? ""} ${th[qKey] ?? ""} ${th[aKey] ?? ""}`.toLowerCase(),
        } satisfies FaqItem;
      }),
    }));
  }, [locale, t]);

  const filtered = useMemo<FaqSection[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({ ...s, items: s.items.filter((it) => it.haystack.includes(q)) }))
      .filter((s) => s.items.length > 0);
  }, [sections, query]);

  const totalMatches = filtered.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="bg-[var(--brand-paper)] text-[var(--brand-ink)]">
      <section className="relative isolate overflow-hidden bg-[var(--brand-pink-soft)]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background:radial-gradient(40rem_28rem_at_90%_-10%,rgba(247,200,213,0.55),transparent_60%)]"
        />
        <div className="relative mx-auto max-w-4xl px-6 pb-12 pt-16 sm:px-8 sm:pb-16 sm:pt-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--brand-rose)] backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {t("faq.eyebrow")}
          </span>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            {t("faq.title")}
          </h1>
          <p className="mt-3 text-base text-[var(--brand-ink)]/75">{t("faq.subtitle")}</p>
          <div className="relative mt-6">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--brand-muted)]"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("faq.search_placeholder")}
              className="h-12 pl-11 shadow-sm"
              aria-label={t("faq.search_placeholder")}
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-12 sm:px-8 sm:py-16">
        {totalMatches === 0 ? (
          <div className="rounded-3xl border border-dashed border-[var(--brand-border)] bg-white p-10 text-center text-[var(--brand-muted)]">
            <p>{t("faq.empty")}</p>
            <Link
              to="/contact-us"
              className="mt-3 inline-block font-semibold text-[var(--brand-rose)] hover:underline"
            >
              {t("footer.contact_us")} →
            </Link>
          </div>
        ) : (
          <div className="space-y-10">
            {filtered.map((section) => (
              <FaqSectionBlock key={section.slug} section={section} />
            ))}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-16 sm:px-8 sm:pb-24">
        <div className="rounded-3xl bg-[var(--brand-ink)] px-8 py-10 text-[var(--brand-paper)] sm:flex sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading text-xl font-bold sm:text-2xl">{t("faq.contact_cta")}</h3>
            <p className="mt-2 max-w-md text-sm text-white/70">{t("faq.contact_cta.body")}</p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 sm:mt-0">
            <Link
              to="/contact-us"
              className="inline-flex h-11 items-center justify-center rounded-full bg-[var(--brand-rose)] px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              {t("footer.contact_us")}
            </Link>
            <Link
              to="/report-a-problem"
              className="inline-flex h-11 items-center justify-center rounded-full border border-white/30 bg-transparent px-5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              {t("footer.report_problem")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqSectionBlock({ section }: { section: FaqSection }) {
  return (
    <div>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-[var(--brand-rose)]">
        {section.heading}
      </h2>
      <Accordion type="multiple" className="mt-4 space-y-3">
        {section.items.map((item) => (
          <AccordionItem key={item.id} value={item.id}>
            <AccordionTrigger>{item.question}</AccordionTrigger>
            <AccordionContent>{item.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
